import { Box, Divider, IconButton, TextField, Tooltip, Typography } from "@mui/material";
import Grid from "@mui/material/Grid/Grid";
import * as Yup from 'yup';
import { useSnackbar } from "notistack";
import Form, { FormDirty, FormErrors, FormFooterAllButtons, FormValidationSchema } from "../../../../components/FormControl/Form/Form";
import Panel from "../../../../components/Panel/Panel";
import PanelBodyFormContent from "../../../../components/Panel/PanelBodyFormContent";
import Task from "../../../../model/Task";
import { useAuthentication } from "../../../../reducer/authentication";
import { useEffect, useState } from "react";
import CommentTask from "../../../../model/CommentTask";
import TicketStatus from "../../../../model/TicketStatus";
import ChatIcon from '@mui/icons-material/Chat';
import CommentFormDialog from "../../../../components/CommentFormDialog";
import TicketHistory, { TicketHistoryEventEmitterType } from "../../../../components/TicketHistory";
import EventEmitter from "../../../../utils/EventEmitter";
import CommentTaskType from "../../../../enum/CommentTaskType";
import TaskRecurrenceType from "../../../../enum/TaskRecurrenceType";
import SelectSearch from "../../../../components/SelectSearch";
import SearchEntity from "../../../../components/SelectSearch/SearchEntity";

export type TaskRecurrenceFormData = {
    recurrence_type: TaskRecurrenceType;
    recurrence_ticket_status_id: number | null;
    recurrence_max: number | null;
    recurrence_value: number | null;
    recurrence_message: string | null;
    comments: CommentTask[];
}

export interface TaskRecurrenceFormProps {
    title: React.ReactNode;
    item?: Task;
    onSubmit: (data: TaskRecurrenceFormData, isDirty?: boolean, submitType?: string | null) => Promise<any>;
    onCancel: () => any;
    onChange: (changed: boolean) => any;
    onUndo: () => any;
}

const eventEmmiter = new EventEmitter<TicketHistoryEventEmitterType>();

export default function TaskRecurrenceForm(props: TaskRecurrenceFormProps) {

    const { title, item, onSubmit, onCancel, onChange, onUndo } = props;

    const { enqueueSnackbar } = useSnackbar();
    const { user } = useAuthentication();

    const [commentDialogOpen, setCommentDialogOpen] = useState<boolean>(false);
    const [isDirty, setDirty] = useState<boolean>(false);

    useEffect(() => {

    }, []);

    const initialValues: TaskRecurrenceFormData = {
        recurrence_type: item?.recurrence_type || TaskRecurrenceType.Inactive,
        recurrence_ticket_status_id: item?.recurrence_ticket_status_id || null,
        recurrence_max: item?.recurrence_max || null,
        recurrence_value: item?.recurrence_value || null,
        recurrence_message: item?.recurrence_message || null,
        comments: item?.comments?.filter(c => c.type === CommentTaskType.Recurrence) || []
    };

    async function onSubmitHandler(values: TaskRecurrenceFormData, dirty: FormDirty<TaskRecurrenceFormData>, errors?: FormErrors<TaskRecurrenceFormData>, submitType?: string | null) {
        const isDirty = Object.values(dirty).reduce((p, c) => p || c, false);

        if (errors) {
            for (var error of Object.values(errors).filter(e => !!e)) {
                enqueueSnackbar(error, { variant: 'error' });
            }
            return;
        }

        await onSubmit(values, isDirty, submitType);
    }

    function openCommentDialog() {
        setCommentDialogOpen(true);
    }

    function closeCommentDialog() {
        setCommentDialogOpen(false);
    }

    function submitCommentDialog(setFieldValue: any, comments: CommentTask[], comment: string, is_public: boolean) {
        if (comment.trim() !== '') {
            let newComment = { id: 0, comment, user_id: user?.id || 0, is_public, type: CommentTaskType.Recurrence };
            comments.push(newComment);
            eventEmmiter.emit('newComment', newComment);
            setFieldValue('comments', comments);
        }
        setCommentDialogOpen(false);
    }

    function setChanges(changed: boolean) {
        setDirty(changed);
        onChange(changed);
    }

    function getValidationSchema() {
        const validationSchema: FormValidationSchema<TaskRecurrenceFormData> = {
            recurrence_value: Yup.number()
                .required()
                .nullable()
                .min(1)
                .label('Dia')
        }

        return validationSchema;
    }

    return (
        <Panel
            panelHeaderTitle={title}
            panelFooterButtons={<FormFooterAllButtons onUndo={onUndo} form="task_post_service_form" onBack={onCancel} isDirty={isDirty} />}
        >
            <PanelBodyFormContent style={{ overflow: 'auto' }}>
                <Grid container alignItems="center" spacing={1}>
                    <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                        <Form<TaskRecurrenceFormData>
                            initialValues={initialValues}
                            validationSchema={getValidationSchema()}
                            onSubmit={onSubmitHandler}
                            onChange={setChanges}
                            style={{ overflow: 'auto' }}
                            id="task_post_service_form"
                        >
                            {({ isSubmitting, values, errors, handleChange, dirty, setFieldValue, handleChangeNumber }) => {
                                return (
                                    <>
                                        <Box marginTop={3} marginBottom={0} className="subtitleGradientBackgorund">
                                            <Typography style={{ flex: 1 }} variant='subtitle2'>
                                                Recorrência
                                            </Typography>
                                        </Box>
                                        <Divider style={{ marginBottom: 4 }} />
                                        <Grid container alignItems="center" spacing={1}>
                                            <Grid item xs={12} sm={12} md={12} lg={6} xl={6}>
                                                <SelectSearch<TaskRecurrenceType>
                                                    name='recurrence_type'
                                                    label='Tipo'
                                                    options={[
                                                        { label: 'Inativo', value: TaskRecurrenceType.Inactive },
                                                        { label: 'Dia fixo', value: TaskRecurrenceType.FixedDay },
                                                        { label: 'Dia fixo da semana', value: TaskRecurrenceType.FixedWeekDay },
                                                        { label: 'Após x dias do encerramento', value: TaskRecurrenceType.AfterDays }

                                                    ]}
                                                    value={values.recurrence_type}
                                                    onChangeHandle={async (_, v) => {
                                                        await setFieldValue('recurrence_type', v || TaskRecurrenceType.Inactive);
                                                        await setFieldValue('recurrence_value', v && v > 0 ? 1 : null);
                                                    }}
                                                    margin='dense'
                                                    fullWidth
                                                    required
                                                />
                                            </Grid>
                                        </Grid>
                                        {values.recurrence_type === TaskRecurrenceType.FixedDay || values.recurrence_type === TaskRecurrenceType.AfterDays ?
                                            <Grid container alignItems="center" spacing={1}>
                                                <Grid item xs={12} sm={12} md={12} lg={6} xl={6}>
                                                    <TextField
                                                        name="recurrence_value"
                                                        label={'Dia(s)'}
                                                        margin="dense"
                                                        variant="outlined"
                                                        fullWidth
                                                        inputProps={{ maxLength: 3 }}
                                                        value={values.recurrence_value || ''}
                                                        onChange={e => {
                                                            const re = /^[0-9\b]+$/;
                                                            if (e.target.value === '' || re.test(e.target.value)) {
                                                                handleChangeNumber(e);
                                                            }
                                                        }}
                                                        error={!!errors.recurrence_value && !!dirty.recurrence_value}
                                                        helperText={!!errors.recurrence_value && !!dirty.recurrence_value && errors.recurrence_value}
                                                        required
                                                    />
                                                </Grid>
                                            </Grid>
                                            : null}
                                        {values.recurrence_type === TaskRecurrenceType.FixedWeekDay ?
                                            <Grid container alignItems="center" spacing={1}>
                                                <Grid item xs={12} sm={12} md={12} lg={6} xl={6}>
                                                    <SelectSearch<number>
                                                        name='recurrence_value'
                                                        label='Dia da semana'
                                                        options={[
                                                            { label: 'Domingo', value: 1 },
                                                            { label: 'Segunda', value: 2 },
                                                            { label: 'Terça', value: 3 },
                                                            { label: 'Quarta', value: 4 },
                                                            { label: 'Quinta', value: 5 },
                                                            { label: 'Sexta', value: 6 },
                                                            { label: 'Sábado', value: 7 }

                                                        ]}
                                                        value={values.recurrence_value}
                                                        onChangeHandle={(_, v) => {
                                                            setFieldValue('recurrence_value', v || 1);
                                                        }}
                                                        margin='dense'
                                                        fullWidth
                                                        required
                                                    />
                                                </Grid>
                                            </Grid>
                                            : null}
                                        <Grid container alignItems="center" spacing={1}>
                                            <Grid item xs={12} sm={12} md={12} lg={6} xl={6}>
                                                <SearchEntity<TicketStatus, 'id', 'name'>
                                                    name="recurrence_ticket_status_id"
                                                    entity="ticketStatus"
                                                    valueField='id'
                                                    labelField="name"
                                                    label="Status ao reabrir"
                                                    value={values.recurrence_ticket_status_id || null}
                                                    onChangeHandle={async (_, v) => {
                                                        await setFieldValue('recurrence_ticket_status_id', v);
                                                    }}
                                                    required
                                                    extraWhere={[['use_in_task', '=', true], ['closed', '=', false]]}
                                                />
                                            </Grid>
                                        </Grid>
                                        <Grid container alignItems="center" spacing={1}>
                                            <Grid item xs={12} sm={12} md={12} lg={6} xl={6}>
                                                <TextField
                                                    name="recurrence_max"
                                                    label={'Número máximo de vezes para reabrir a tarefa'}
                                                    margin="dense"
                                                    variant="outlined"
                                                    fullWidth
                                                    inputProps={{ maxLength: 3 }}
                                                    value={values.recurrence_max || ''}
                                                    onChange={e => {
                                                        const re = /^[0-9\b]+$/;
                                                        if (e.target.value === '' || re.test(e.target.value)) {
                                                            handleChangeNumber(e);
                                                        }
                                                    }}
                                                    error={!!errors.recurrence_max && !!dirty.recurrence_max}
                                                    helperText={'Vazio = infinito'}
                                                />
                                            </Grid>
                                        </Grid>
                                        <Grid container alignItems="center" spacing={1}>
                                            <Grid item xs={12} sm={12} md={12} lg={6} xl={6}>
                                                <TextField
                                                    name="recurrence_message"
                                                    label={'Mensagem adicional'}
                                                    margin="dense"
                                                    variant="outlined"
                                                    fullWidth
                                                    multiline
                                                    rows={6}
                                                    value={values.recurrence_message || ''}
                                                    onChange={(e) => setFieldValue('recurrence_message', e.target.value)}
                                                    error={!!errors.recurrence_message}
                                                />
                                            </Grid>
                                        </Grid>
                                        <Box marginTop={3} marginBottom={0} display="flex">
                                            <Typography variant='subtitle2' style={{ flex: 1 }} className="subtitleGradientBackgorund">Histórico da recorrência</Typography>
                                            {/*<Tooltip placement="top" title={'Adicionar comentário'}>
                                                <IconButton
                                                    size='small'
                                                    color="inherit"
                                                    aria-label={'Adicionar comentário'}
                                                    onClick={() => openCommentDialog()}
                                                >
                                                    <ChatIcon />
                                                </IconButton>
                                            </Tooltip>*/}
                                        </Box>
                                        <CommentFormDialog
                                            commentDialogOpen={commentDialogOpen}
                                            onCancel={() => closeCommentDialog()}
                                            onSubmit={(comment, isPublic) => submitCommentDialog(setFieldValue, values.comments, comment, isPublic)}
                                        />
                                    </>
                                );
                            }}
                        </Form>
                        <Divider style={{ marginBottom: 4 }} />
                        <TicketHistory
                            key={item?.id}
                            type="taskRecurrence"
                            id={item?.id}
                            comments={item?.comments?.filter(c => c.type === CommentTaskType.Recurrence) || []}
                            ticketHistoryEventListener={eventEmmiter}
                        />
                    </Grid>
                </Grid>
            </PanelBodyFormContent>
        </Panel>
    )
}